import type { FormField } from '../../interfaces/form.interface';
import { DropdownData, FilteredValue, InputType, Value } from '../../interfaces/input.interface';
import { setDropdownSelected } from '../dropdown-data.utils';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';

type DropdownOption<T> = Value<T> | FilteredValue<T>;

/** Keeps the legacy dropdown selection compatibility store in sync with the Angular control value. */
export class DropdownSelectedValueService<T> {
    public applySelected(field: FormField<T> | undefined, value: unknown): void {
        const dropdownData = field?.input.data as DropdownData<T> | undefined;

        if (!dropdownData) {
            return;
        }

        if (this.isMultiSelect(field)) {
            setDropdownSelected(dropdownData, this.normalizeMultiSelectedValue(field, dropdownData, value) as T[]);
            return;
        }

        setDropdownSelected(dropdownData, this.normalizeSingleSelectedValue(field, dropdownData, value) as T | null);
    }

    private normalizeSingleSelectedValue(
        field: FormField<T> | undefined,
        dropdownData: DropdownData<T>,
        value: unknown,
    ): unknown | null {
        if (!ReactiveFormValueUtils.hasNonEmptyValue(value)) {
            return null;
        }

        return this.resolveRawOptionValue(field, dropdownData, value) ?? value;
    }

    private normalizeMultiSelectedValue(
        field: FormField<T> | undefined,
        dropdownData: DropdownData<T>,
        value: unknown,
    ): unknown[] {
        const values = Array.isArray(value) ? value : [];

        return values.map((item) => this.resolveRawOptionValue(field, dropdownData, item) ?? item);
    }

    private resolveRawOptionValue(
        field: FormField<T> | undefined,
        dropdownData: DropdownData<T>,
        value: unknown,
    ): unknown | null {
        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
        const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData as DropdownData<unknown>) as Array<DropdownOption<T>>;
        const comparableValue = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);

        if (comparableValue === null) {
            return null;
        }

        const comparableKey = String(comparableValue);
        const option = options.find((candidate) =>
            ReactiveFormValueUtils.getOptionComparableKeys(candidate, primaryKey, identityKeys).includes(comparableKey)
        );

        return option?.raw ?? option?.value ?? null;
    }

    private isMultiSelect(field: FormField<T> | undefined): boolean {
        return field?.input?.type === InputType.MULTISELECT_SEARCH || field?.input?.type === InputType.MULTISELECT;
    }
}
